import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { FormContactDatePicker } from 'src/components/FormPart/FormContactDatePicker';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import React, { Fragment, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAddContactCustomField, useDeleteContactCustomField } from 'src/api/customFields';
import { useGetUsersApi } from 'src/api/users';
import { GetUsersParams } from 'src/api/users/types';
import SelectWithSearch from 'src/components/SelectWithSearch/view';
import { GroupOptionLabel, formatGroupContactCountSuffix } from 'src/components/GroupOptionLabel';
import { ContactCustomField, ContactDefaultCustomField, User } from 'src/constants/types';
import { createContactValidationSchema, ValidationErrors } from 'src/constants/validations';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useContactsApi } from 'src/modules/contacts/api';
import { Contact } from 'src/modules/contacts/types';
import { SaveAndStartJoinCampaignButton } from 'src/modules/campaigns/components/SaveAndStartJoinCampaignButton';
import { useGroupsApi } from 'src/modules/groups/api';
import { Group } from 'src/modules/groups/types';
import {
  useMarkContactAsValidDialog,
  useSpamContactDialog,
} from 'src/modules/inbox/components';
import * as Yup from 'yup';
import { FormDateTimePicker } from 'src/components/FormPart/FormDateTimePicker';
import { FormNumberInput } from 'src/components/FormPart/FormNumberInput';
import { FormTextInput } from 'src/components/FormPart/FormTextInput';
import { Panel, BhiDialog } from 'src/UIKit';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';

type CreateContactModalType = {
  show: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  contactData?: Contact | null;
  groupData?: Group | null;
  customFields?: ContactDefaultCustomField[] | null;
  refresh: () => void;
};

const timeZones = moment.tz.zonesForCountry('US');
const currentTimezone = timeZones.includes(moment.tz.guess()) ? moment.tz.guess() : timeZones[0];

const initialValues: {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  timeZone: string;
  birthdate: string | undefined;
  assignedTo: string;
  metadata: ContactCustomField[];
  validStatus?: string;
  isSpam?: boolean;
} = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  timeZone: currentTimezone,
  birthdate: undefined,
  assignedTo: '',
  metadata: [] as ContactCustomField[],
};

const getContactSaveErrors = (e: unknown, includeDetails: boolean): ValidationErrors | null => {
  const { error, message } = (e ?? {}) as { error?: string; message?: string };
  if (error === 'conflict' || message?.includes('duplicate')) {
    return { duplicate: 'You have duplicated contacts, please check and then submit.' };
  }
  if (error === 'validation') {
    const base = 'Provided contact are not valid, please check and then submit.';
    return { validation: includeDetails ? `${base} ${message}` : base };
  }
  return null;
};

export function CreateContactModal({
  show,
  setShowModal,
  groupData,
  refresh,
  contactData,
  customFields,
}: CreateContactModalType) {
  const [formValues, setFormValues] = useState(initialValues);
  const [existedGroupIds, setExistedGroupIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedGroupId, setSelectedGroup] = useState<string>(groupData?.id || '');
  const [pendingContacts, setPendingContacts] = useState<(typeof formValues)[]>([]);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [defaultGroupId, setDefaultGroupId] = useState<string>('');
  const tableRef = useRef<HTMLTableElement>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const dateForm = useForm<{ birthdate: string }>({ defaultValues: { birthdate: '' } });
  const metadataDateForm = useForm<Record<string, string>>({});
  const { getGroups, addContactsToGroup, removeContactsFromGroup } = useGroupsApi();
  const getUsers = useGetUsersApi();
  const selectedCompanyId: string | undefined = useSelector(getCurrentCompanyId);
  const addContactCustomField = useAddContactCustomField();
  const deleteContactCustomField = useDeleteContactCustomField();
  const { SpamContactDialog, SpamButton } = useSpamContactDialog();
  const { MarkContactAsValidDialog, MarkContactAsValidButton } = useMarkContactAsValidDialog();
  const {
    getContacts: fetchContacts,
    createContacts,
    assignUser,
    updateContactInfo,
    getContactGroups,
  } = useContactsApi();

  const handleClose = () => {
    setFormValues(initialValues);
    setErrors({});
    dateForm.reset({ birthdate: '' });
    metadataDateForm.reset({});
    setShowModal(false);
    refresh();
  };

  useEffect(() => {
    if (selectedCompanyId) {
      const usersQuery: GetUsersParams = {
        companyId: selectedCompanyId,
        limit: 10000,
        offset: 0,
        sort: 'last_name_asc',
      };
      getUsers(usersQuery).then(response => {
        setUsers(response.data.items);
      });
      getGroups({
        companyId: selectedCompanyId,
        limit: 1000,
        offset: 0,
        statuses: 'active, archived',
      })
        .then(({ data }) => {
          const defaultGroup = data.items?.find(g => g.type === 'default');
          if (defaultGroup) {
            setDefaultGroupId(defaultGroup.id);
          }
          setGroups(data.items || []);
        })
        .catch(e => {
          console.log(e);
        });
      if (contactData?.id) {
        getContactGroups(selectedCompanyId, contactData.id, {
          offset: 0,
          limit: 100000,
        }).then(response => {
          const ids = response.data.items.map(m => m.id);
          setExistedGroupIds(ids);
          setSelectedGroupIds(ids);
        });
      }
    }
  }, [selectedCompanyId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const formatUserName = (user: User): string => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`;
    }

    return user.phoneNumber;
  };

  const handleSelectChange = ({ target }: SelectChangeEvent<string>) => {
    setFormValues({ ...formValues, timeZone: target.value });
  };

  const updateGroups = () => {
    if (selectedCompanyId) {
      if (contactData?.id) {
        const addedGroupIds = selectedGroupIds.filter(id => !existedGroupIds.includes(id));
        const removedGroupIds = existedGroupIds.filter(
          id => !selectedGroupIds.includes(id) && id !== defaultGroupId,
        );

        Promise.all([
          ...addedGroupIds.map(id => addContactsToGroup(selectedCompanyId, id, [contactData.id])),
          ...removedGroupIds.map(id =>
            removeContactsFromGroup(selectedCompanyId, id, [contactData.id]),
          ),
        ])
          .then(() => {
            handleClose();
          })
          .catch(e => console.log(e));
      }
    }
  };

  useEffect(() => {
    setSelectedGroup(groupData?.id || '');
  }, [groupData?.id]);

  // Sync dateForm changes to formValues
  useEffect(() => {
    const subscription = dateForm.watch(value => {
      if (value.birthdate !== formValues.birthdate) {
        setFormValues(prev => ({ ...prev, birthdate: value.birthdate || '' }));
      }
    });
    return () => subscription.unsubscribe();
  }, [dateForm, formValues.birthdate]);

  // Sync metadata date form changes to formValues
  useEffect(() => {
    const subscription = metadataDateForm.watch(value => {
      setFormValues(prev => {
        let hasChanges = false;
        const updatedMetadata = prev.metadata.map(m => {
          const nextValue = value[m.metadataId] || '';
          if (nextValue !== m.value) {
            hasChanges = true;
            return { ...m, value: nextValue };
          }
          return m;
        });

        if (!hasChanges) {
          return prev;
        }

        return {
          ...prev,
          metadata: updatedMetadata,
        };
      });
    });
    return () => subscription.unsubscribe();
  }, [metadataDateForm]);

  const validate = async (values: typeof formValues) => {
    try {
      await createContactValidationSchema.validate(values, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleAddContact = async () => {
    const isValid = await validate(formValues);
    if (!isValid) return;

    // Check duplicates in pending contacts (compare normalized number without +1)
    const currentPhone = (formValues.phoneNumber || '').replace('+1', '');
    const hasDuplicateInPending = pendingContacts.some(
      c => (c.phoneNumber || '').replace('+1', '') === currentPhone,
    );
    if (hasDuplicateInPending) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: 'A contact with the same phone number already added.',
      }));
      return;
    }

    // Check existing contacts within the company by phone number
    if (selectedCompanyId && currentPhone) {
      try {
        const { data } = await fetchContacts({
          companyId: selectedCompanyId,
          phoneNumber: '+1' + currentPhone,
          limit: 1,
          offset: 0,
        });
        if (data.items && data.items.length > 0) {
          const found = data.items[0];
          if (!contactData?.id || found.id != contactData.id) {
            setErrors(prev => ({
              ...prev,
              phoneNumber: 'A contact with the same phone number already exist within the company.',
            }));
            return;
          }
        }
      } catch (e) {
        // Silently ignore lookup failures for UX; user can still attempt add
      }
    }

    setPendingContacts([...pendingContacts, formValues]);

    const resetMetadata =
      customFields && customFields.length > 0
        ? customFields.map(
            customField =>
              ({
                id: '',
                contactId: '',
                metadataId: customField.metadataId,
                type: customField.type,
                name: customField.name,
                description: customField.description,
                value: '',
              } as ContactCustomField),
          )
        : [];
    setFormValues({
      ...initialValues,
      metadata: resetMetadata,
    });
    dateForm.reset({ birthdate: '' });
    metadataDateForm.reset({});
    setErrors({});
  };

  const handleContactRemove = (index: number) => {
    const newItems = [...pendingContacts.slice(0, index), ...pendingContacts.slice(index + 1)];
    setPendingContacts(newItems);
  };

  const selectedGroup =
    groups.find(item => item.id === selectedGroupId) ??
    (groupData?.id === selectedGroupId ? groupData : undefined);
  // Every contact is put in the default group anyway, so only another group needs adding to.
  const groupIdToAdd =
    selectedGroupId && selectedGroup?.type !== 'default' ? selectedGroupId : undefined;

  /** Resolves to the created contacts, or to nothing when they were not created or not added to the group. */
  const handleSaveContacts = async (): Promise<string[] | undefined> => {
    if (!pendingContacts.length) {
      handleClose();
      return;
    }
    if (!selectedCompanyId || savingRef.current) return;

    savingRef.current = true;
    setIsSaving(true);

    const metadata = pendingContacts[0].metadata;
    let assignedUserTo = '';
    const contacts = pendingContacts.map(item => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { metadata, assignedTo, ...data } = { ...item, phoneNumber: '+1' + item.phoneNumber };
      assignedUserTo = assignedTo;
      data.birthdate =
        data.birthdate && dayjs(data.birthdate).isValid()
          ? dayjs(data.birthdate.substring(0, 10)).format('YYYY-MM-DD')
          : undefined;
      return data;
    });

    try {
      let created: Contact[];
      try {
        ({ data: created } = await createContacts(selectedCompanyId, contacts));
      } catch (e) {
        const saveErrors = getContactSaveErrors(e, true);
        if (saveErrors) {
          setErrors(saveErrors);
        } else {
          reportErrorToUser(e, 'Could not save contacts. Please try again.');
        }
        return;
      }

      // Contacts already exist at this point, so a failed follow-up must not block closing.
      // Some statuses (e.g. 409 from custom fields) skip the generic toast, so every failure is
      // reported here; the toast is deduplicated by message.
      const followUps: Promise<unknown>[] = [];
      created.forEach(contact => {
        if (assignedUserTo) {
          followUps.push(assignUser(selectedCompanyId, contact.id, assignedUserTo));
        }
        metadata.forEach(m => {
          if (m.value) {
            followUps.push(
              addContactCustomField(
                [{ metadataId: m.metadataId, value: m.value.toString() }],
                contact.id,
              ),
            );
          } else if (m.id) {
            followUps.push(deleteContactCustomField([m.metadataId], contact.id));
          }
        });
      });
      const results = await Promise.allSettled(followUps);
      results.forEach(result => {
        if (result.status === 'rejected') {
          reportErrorToUser(
            result.reason,
            'Contacts were created, but some details could not be saved.',
          );
        }
      });

      const ids = created.map(({ id }) => id);
      let addedToGroup = true;
      if (groupIdToAdd) {
        try {
          await addContactsToGroup(selectedCompanyId, groupIdToAdd, ids, {
            skipGenericToast: true,
          });
        } catch (e) {
          addedToGroup = false;
          reportErrorToUser(
            e,
            `Contacts were created but could not be added to ${
              selectedGroup?.name ? `"${selectedGroup.name}"` : 'the group'
            }.`,
          );
        }
      }

      setPendingContacts([]);
      handleClose();
      // The join campaigns offered to start include the group's, so none is offered if the contacts did not get in.
      return addedToGroup ? ids : undefined;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleSelectedGroupsChange = (
    _: SyntheticEvent<Element, Event> | undefined,
    items: { text: string; value: string }[],
  ) => {
    const ids = items?.map(({ value }) => value) || [];
    setSelectedGroupIds(ids);
  };

  const handleUpdateContact = () => {
    if (selectedCompanyId && contactData?.id) {
      // Pre-check for duplicates by phone inside the company, excluding current contact
      const currentPhone = (formValues.phoneNumber || '').replace('+1', '');
      if (currentPhone) {
        fetchContacts({
          companyId: selectedCompanyId,
          phoneNumber: '+1' + currentPhone,
          limit: 1,
          offset: 0,
        })
          .then(({ data }) => {
            const found = data.items && data.items.length > 0 ? data.items[0] : undefined;
            if (found && found.id !== contactData.id) {
              setErrors(prev => ({
                ...prev,
                phoneNumber:
                  'A contact with the same phone number already exist within the company.',
              }));
              return;
            }
            proceedUpdate();
          })
          .catch(() => {
            // On lookup error, proceed with update; server-side will still validate
            proceedUpdate();
          });
        return;
      }

      // No phone provided; proceed
      proceedUpdate();
    }
  };

  const proceedUpdate = () => {
    if (selectedCompanyId && contactData?.id) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { metadata, assignedTo, isSpam, ...data } = formValues;
      data.birthdate =
        data.birthdate && dayjs(data.birthdate).isValid()
          ? dayjs(data.birthdate.substring(0, 10)).format('YYYY-MM-DD')
          : undefined;
      updateContactInfo(selectedCompanyId, contactData?.id, data)
        .then(() => {
          assignUser(selectedCompanyId, contactData?.id, assignedTo);

          formValues.metadata.map(m => {
            if (m.value) {
              addContactCustomField(
                [
                  {
                    metadataId: m.metadataId,
                    value: m.value.toString(),
                  },
                ],
                contactData.id,
              );
            }
            if (!m.value && m.id) {
              deleteContactCustomField([m.metadataId], contactData.id);
            }
          });

          updateGroups();

          handleClose();
          refresh();
        })
        .catch(e => {
          const saveErrors = getContactSaveErrors(e, false);
          if (saveErrors) {
            setErrors(saveErrors);
          } else {
            reportErrorToUser(e, 'Could not update contact. Please try again.');
          }
        });
    }
  };

  useEffect(() => {
    if (contactData) {
      setFormValues({
        firstName: contactData.firstName,
        lastName: contactData.lastName,
        email: contactData.email,
        birthdate: contactData?.birthdate,
        timeZone: contactData.timeZone,
        phoneNumber: contactData.phoneNumber,
        metadata: contactData.metadata,
        assignedTo: contactData.assignedTo || '',
        validStatus: contactData.validStatus,
        isSpam: contactData.isSpam?.value,
      });
      dateForm.reset({ birthdate: contactData?.birthdate || '' });

      // Initialize metadata
      const metadataDefaults: Record<string, string> = {};
      contactData.metadata.forEach(m => {
        metadataDefaults[m.metadataId] = m.value || '';
      });
      metadataDateForm.reset(metadataDefaults);
    } else {
      if (customFields) {
        if (customFields && customFields.length > 0) {
          setFormValues({
            ...initialValues,
            metadata: customFields.map(
              customField =>
                ({
                  id: '',
                  contactId: '',
                  metadataId: customField.metadataId,
                  type: customField.type,
                  name: customField.name,
                  description: customField.description,
                  value: '',
                } as ContactCustomField),
            ),
          });
        }
      }
      metadataDateForm.reset({});
    }
  }, [contactData?.id]);

  return (
    <Fragment>
      <SpamContactDialog />
      <MarkContactAsValidDialog />
      <BhiDialog
        onClose={() => !savingRef.current && handleClose()}
        closeButtonDisabled={isSaving}
        aria-labelledby="customized-dialog-title"
        open={show}
        keepMounted={false}
        paperSx={{ maxWidth: 'unset' }}
        title={contactData ? 'Edit Contact' : 'Add Contacts'}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
          <Stack direction="row" width="100%">
            <Stack component={contactData ? 'div' : Panel} width={contactData ? 450 : 350}>
              <Stack sx={{ maxHeight: 530, overflowY: 'auto', pt: 0.625 }}>
                <TextField
                  id="outlined-basic"
                  label="First Name"
                  name="firstName"
                  variant="outlined"
                  value={formValues.firstName}
                  onChange={handleChange}
                />
                <TextField
                  id="outlined-basic"
                  style={{ marginTop: '20px' }}
                  label="Last Name"
                  name="lastName"
                  variant="outlined"
                  value={formValues.lastName}
                  onChange={handleChange}
                />
                <TextField
                  id="outlined-basic"
                  sx={{ mt: 2.5 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+1</InputAdornment>,
                  }}
                  name="phoneNumber"
                  type="tel"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  label="Phone Number"
                  variant="outlined"
                  value={formValues.phoneNumber.replace('+1', '')}
                  onChange={handleChange}
                  error={!!errors?.phoneNumber}
                  helperText={errors?.phoneNumber || ''}
                />
                {contactData?.id && (
                  <Box mt={1.25}>
                    {groups && (
                      <SelectWithSearch
                        disableClearable={true}
                        label="Groups"
                        placeholder="Groups"
                        style={{
                          width: '100%',
                          margin: '0 0 20px 0',
                          maxHeight: '250px',
                          paddingTop: '10px',
                          overflow: 'auto',
                        }}
                        options={[
                          ...(groups
                            ?.filter(g => g.type !== 'default')
                            .sort((a, b) =>
                              a.type.toLowerCase().localeCompare(b.type.toLowerCase()),
                            )
                            .map(({ name, id, status, type, validContactCount, joinCampaigns }) => ({
                              text: name || '',
                              secondaryText: formatGroupContactCountSuffix(
                                validContactCount,
                                joinCampaigns?.length,
                              ),
                              value: id,
                              isArchived: status === 'archived',
                              isDisabled: type === 'dynamic',
                            })) || []),
                        ]}
                        values={selectedGroupIds}
                        showArchived={true}
                        handleSelectChange={(e, items) => handleSelectedGroupsChange(e, items)}
                      />
                    )}
                  </Box>
                )}
                <TextField
                  id="outlined-basic"
                  style={{ marginTop: '20px' }}
                  label="Email"
                  name="email"
                  variant="outlined"
                  value={formValues.email}
                  onChange={handleChange}
                  error={!!errors?.email}
                  helperText={errors?.email || ''}
                />
                <Box mt={2.5}>
                  <FormContactDatePicker
                    form={dateForm}
                    fieldName="birthdate"
                    label="Birthdate"
                    placeholder="Enter Birth date"
                  />
                </Box>

                <FormControl sx={{ mt: 2.5 }}>
                  <InputLabel htmlFor="bhi-simple-select">Timezone</InputLabel>
                  <Select
                    labelId="bhi-simple-select-label"
                    id="bhi-simple-select"
                    value={formValues.timeZone}
                    label="Timezone"
                    onChange={handleSelectChange}
                    sx={{ minWidth: 150 }}
                  >
                    {timeZones.map(item => {
                      return (
                        <MenuItem value={item} key={item}>
                          {item}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <FormControl sx={{ mt: 2.5 }}>
                  <InputLabel htmlFor="bhi-simple-select-user">Assigned To</InputLabel>
                  <Select
                    labelId="bhi-simple-select-label"
                    id="bhi-simple-select-user"
                    value={formValues.assignedTo}
                    label="Assigned To"
                    onChange={({ target }) =>
                      setFormValues({ ...formValues, assignedTo: target.value })
                    }
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value={''} key={'unassign'}>
                      Unassigned
                    </MenuItem>
                    {users?.map(item => {
                      return (
                        <MenuItem value={item.id} key={item.id}>
                          {formatUserName(item)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {formValues.metadata
                  .sort((a, b) =>
                    a.description.toLowerCase().localeCompare(b.description.toLowerCase()),
                  )
                  .map(m => {
                    switch (m.type) {
                      case 'date':
                        return (
                          <Box key={m.metadataId} mt={2.5}>
                            <FormContactDatePicker
                              key={m.metadataId}
                              form={metadataDateForm}
                              fieldName={m.metadataId}
                              label={m.description}
                              placeholder={m.description}
                            />
                          </Box>
                      );
                      case 'datetime':
                        return (
                          <Box key={m.metadataId} mt={2.5}>
                            <FormDateTimePicker
                              key={m.metadataId}
                              form={metadataDateForm}
                              fieldName={m.metadataId}
                              label={m.description}
                              placeholder={m.description}
                            />
                          </Box>
                        );
                      case 'dollars':
                        return (
                          <FormNumberInput
                            key={m.metadataId}
                            form={metadataDateForm}
                            fieldName={m.metadataId}
                            label={m.description}
                            placeholder={m.description}
                            format={{
                              maximumFractionDigits: 2,
                              minimumFractionDigits: 0,
                              style: 'currency',
                              currency: 'USD',
                            }}
                            sx={{ mt: 2.5 }}
                            variant="outlined"
                          />
                        );
                      case 'number':
                        return (
                          <FormNumberInput
                            key={m.metadataId}
                            form={metadataDateForm}
                            fieldName={m.metadataId}
                            label={m.description}
                            placeholder={m.description}
                            sx={{ mt: 2.5 }}
                            variant="outlined"
                          />
                        );
                      default:
                        return (
                          <FormTextInput
                            key={m.metadataId}
                            form={metadataDateForm}
                            fieldName={m.metadataId}
                            label={m.description}
                            placeholder={m.description}
                            variant="outlined"
                          />
                        );
                    }
                  })}
              </Stack>
              {!contactData && (
                <Button
                  variant="contained"
                  style={{ width: '100px', marginTop: '20px' }}
                  onClick={handleAddContact}
                  disabled={isSaving}
                >
                  Add
                </Button>
              )}
            </Stack>
            {!contactData && (
              <Stack component={Panel} width={800} ml={4}>
                <FormControl>
                  <InputLabel htmlFor="bhi-simple-select-group">Group</InputLabel>
                  <Select
                    labelId="bhi-simple-select-label"
                    id="bhi-simple-select-group"
                    value={selectedGroupId}
                    label={'Group'}
                    onChange={({ target }) => target.value && setSelectedGroup(target.value)}
                    sx={{ minWidth: 150 }}
                  >
                    {groups?.map(item => {
                      return (
                        <MenuItem value={item.id} key={item.id} disabled={item.type === 'dynamic'}>
                          <GroupOptionLabel
                            name={item.name || ''}
                            validContactCount={item.validContactCount}
                            joinCampaignCount={item.joinCampaigns?.length}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Table ref={tableRef} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone Number</TableCell>
                      <TableCell>Birthdate</TableCell>
                      <TableCell>Timezone</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingContacts.map(
                      ({ firstName, lastName, email, phoneNumber, birthdate, timeZone }, index) => {
                        const formatBirthdate = (date: string | undefined) => {
                          if (!date) return '-';
                          const dateParts = date.split('-');
                          if (dateParts.length === 3 && dateParts[0] === '2400') {
                            // Show ---- for year when year is 2400 (no year specified)
                            return `${dateParts[1]}/${dateParts[2]}/----`;
                          }
                          return date;
                        };

                        return (
                          <TableRow key={phoneNumber}>
                            <TableCell component="th" scope="row">
                              {firstName || lastName ? firstName + ' ' + lastName : '-'}
                            </TableCell>
                            <TableCell>{email || '-'}</TableCell>
                            <TableCell>{phoneNumber}</TableCell>
                            <TableCell>{formatBirthdate(birthdate)}</TableCell>
                            <TableCell>{timeZone || '-'}</TableCell>
                            <TableCell>
                              <Button onClick={() => handleContactRemove(index)}>
                                <DeleteIcon />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      },
                    )}
                  </TableBody>
                </Table>
              </Stack>
            )}
          </Stack>
          {errors?.duplicate && <FormHelperText error>{errors?.duplicate}</FormHelperText>}
          {errors?.validation && <FormHelperText error>{errors?.validation}</FormHelperText>}
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" mt={1.25}>
            {contactData && contactData.validStatus === 'valid' && (
              <SpamButton
                selectedCompanyId={selectedCompanyId!}
                contactId={contactData.id}
                isSpam={!!formValues.isSpam}
                nextAction={() => {
                  setFormValues(formValues => ({
                    ...formValues,
                    isSpam: !formValues.isSpam,
                  }));
                  setShowModal(false);
                  refresh();
                }}
              />
            )}
            {contactData && contactData.validStatus === 'invalid' && (
              <MarkContactAsValidButton
                selectedCompanyId={selectedCompanyId!}
                contact={contactData}
                nextAction={() => {
                  setFormValues(formValues => ({
                    ...formValues,
                    validStatus: 'valid',
                  }));
                  setShowModal(false);
                  refresh();
                }}
              />
            )}
            {!contactData && (
              <FormControlLabel
                slotProps={{ typography: { fontSize: 14 } }}
                sx={{ ml: 'auto' }}
                control={<Checkbox onChange={event => setIsAccepting(event.target.checked)} />}
                label="I confirm that I have received express consent to message the contacts that I am adding."
              />
            )}
            {contactData ? (
              <Button variant="contained" style={{ width: '180px' }} onClick={handleUpdateContact}>
                Update Contact
              </Button>
            ) : (
              // Only show Save Contacts button when there are pending contacts to save
              <SaveAndStartJoinCampaignButton
                label="Save Contacts"
                mainButtonWidth="180px"
                disabled={isSaving || !isAccepting || pendingContacts.length === 0}
                groupIds={groupIdToAdd ? [groupIdToAdd] : []}
                includeDefaultGroup
                onSave={handleSaveContacts}
              >
                {isSaving ? 'Saving…' : 'Save Contacts'}
              </SaveAndStartJoinCampaignButton>
            )}
          </Stack>
        </DialogContent>
      </BhiDialog>
    </Fragment>
  );
}
